import type { ReactElement } from 'react';
import React from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import './pagination.scss';

type Props = {
  pageCount: number;
  previousPage(): void;
  nextPage(): void;
  gotoPage(n: number): void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageIndex: number;
};

export default function TablePagination(props: Props): ReactElement {
  const { pageCount, pageIndex, gotoPage, canPreviousPage, canNextPage, previousPage, nextPage } =
    props;

  const pages = React.useMemo(() => {
    const pagesArray: ReactElement[] = [];
    for (let p = 1; p <= pageCount; p += 1) {
      pagesArray.push(
        <PaginationItem active={pageIndex + 1 === p} key={p}>
          <PaginationLink type="button" onClick={(): void => gotoPage(p - 1)}>
            {p}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pagesArray;
  }, [pageCount, pageIndex, gotoPage]);

  // Figure out start and end of visible range so that 5 pages are always visible
  const pageEnd = Math.max(pageIndex + 3, 5);
  const pageStart = Math.max(pageIndex + Math.min(pageCount - pageEnd - 2, -2), 0);

  return (
    <Pagination className="pagination">
      <PaginationItem disabled={!canPreviousPage}>
        <PaginationLink
          type="button"
          first
          onClick={(): void => gotoPage(0)}
          disabled={!canPreviousPage}
        />
      </PaginationItem>
      <PaginationItem disabled={!canPreviousPage}>
        <PaginationLink type="button" previous onClick={(): void => previousPage()} />
      </PaginationItem>
      {pages.slice(pageStart, pageEnd)}
      <PaginationItem disabled={!canNextPage}>
        <PaginationLink type="button" next onClick={(): void => nextPage()} />
      </PaginationItem>
      <PaginationItem disabled={!canNextPage}>
        <PaginationLink type="button" last onClick={(): void => gotoPage(pageCount - 1)} />
      </PaginationItem>
    </Pagination>
  );
}
